import React, { useState } from 'react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Copy, ExternalLink, Globe, Upload } from 'lucide-react';
import { setCustomDomain } from '../api';

interface AppHostingCardProps {
  appId: string;
  tenantId: string;
  hostingSettings?: {
    enabled?: boolean;
    url?: string;
    subdomain_url?: string;
    custom_domain?: string | null;
    custom_domain_ssl?: string | null;
    custom_domain_environment?: 'production' | 'sandbox' | null;
    last_deployed_at?: string | null;
    deploy_version?: string | null;
    deploy_size_bytes?: number | null;
    dns_instructions?: {
      type: string;
      host: string;
      value: string;
      ttl: number;
      records?: Array<{ type: string; host: string; value: string; ttl: number; fqdn?: string }>;
      tls_path?: string;
      note?: string;
    } | null;
    sandbox?: {
      last_deployed_at?: string | null;
      deploy_version?: string | null;
      deploy_size_bytes?: number | null;
    } | null;
  };
  onRefresh?: () => void;
}

export function AppHostingCard({ appId, tenantId, hostingSettings, onRefresh }: AppHostingCardProps) {
  const [domainInput, setDomainInput] = useState(hostingSettings?.custom_domain || '');
  const [bindEnv, setBindEnv] = useState<'production' | 'sandbox'>(
    hostingSettings?.custom_domain_environment === 'sandbox' ? 'sandbox' : 'production',
  );
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copied, setCopied] = useState<string | null>(null);

  const baseOrigin = typeof window !== 'undefined' ? window.location.origin : 'https://dev.devcockpit.ai';
  const publicBaseUrl = `${baseOrigin}/public`;
  const subdomainUrl = hostingSettings?.subdomain_url || `https://${appId}.devcockpit.ai/`;
  const customDomain = hostingSettings?.custom_domain;
  const isDeployed = !!hostingSettings?.last_deployed_at;
  const sslStatus = hostingSettings?.custom_domain_ssl;

  const defaultHostedUrl = hostingSettings?.url || `${baseOrigin}/apps/${appId}/`;
  const sandboxUrl = `https://sandbox.devcockpit.ai/apps/${appId}/`;
  const productionUrl = `https://devcockpit.ai/apps/${appId}/`;
  const sandboxDeployedAt = hostingSettings?.sandbox?.last_deployed_at;
  const sandboxVersion = hostingSettings?.sandbox?.deploy_version;
  const isSandboxDeployed = !!sandboxDeployedAt;
  const isProductionDeployed = isDeployed;
  const deployCommand = 'dc__repo_push(...) then dc__deploy_app({ commit, build_only: true }) then dc__deploy_app({ commit })';

  function copyToClipboard(text: string, label: string) {
    navigator.clipboard.writeText(text);
    setCopied(label);
    setTimeout(() => setCopied(null), 2000);
  }

  async function handleSaveDomain() {
    const domain = domainInput.trim().toLowerCase();
    if (!domain) return;
    setSaving(true);
    setError(null);
    try {
      await setCustomDomain(appId, domain, { environment: bindEnv });
      onRefresh?.();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  }

  function formatBytes(bytes: number): string {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  }

  return (
    <Card>
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-sm">App Hosting</h3>
          <div className="flex items-center gap-1.5">
            <Badge variant={isSandboxDeployed ? 'secondary' : 'outline'} className="text-[10px]">
              Sandbox {isSandboxDeployed ? '✓' : '—'}
            </Badge>
            <Badge variant={isProductionDeployed ? 'default' : 'secondary'}>
              {isProductionDeployed ? 'Live' : 'Not Deployed'}
            </Badge>
          </div>
        </div>

        {/* Sandbox URL */}
        <div className="rounded-md border border-amber-200 bg-amber-50/50 p-2 space-y-1">
          <div className="flex items-center justify-between">
            <label className="text-xs font-medium text-amber-800">Sandbox</label>
            {isSandboxDeployed && sandboxVersion && (
              <span className="text-[10px] text-amber-700">v{sandboxVersion}</span>
            )}
          </div>
          <div className="flex items-center gap-1">
            <code className="text-xs bg-white border border-amber-200 px-2 py-1 rounded flex-1 truncate text-amber-900">{sandboxUrl}</code>
            <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={() => copyToClipboard(sandboxUrl, 'sandbox')}>
              <Copy className="h-3.5 w-3.5" />
            </Button>
            <a href={sandboxUrl} target="_blank" rel="noopener noreferrer">
              <Button variant="ghost" size="icon" className={`h-7 w-7 shrink-0 ${!isSandboxDeployed ? 'opacity-40' : ''}`}>
                <ExternalLink className="h-3.5 w-3.5" />
              </Button>
            </a>
          </div>
          {isSandboxDeployed && sandboxDeployedAt && (
            <p className="text-[10px] text-amber-700">
              Last deployed: {new Date(sandboxDeployedAt).toLocaleString()}
            </p>
          )}
          {!isSandboxDeployed && (
            <p className="text-[10px] text-amber-600">No sandbox build yet — call dc__deploy_app to deploy.</p>
          )}
        </div>

        {/* Production URL */}
        <div className="rounded-md border border-emerald-200 bg-emerald-50/50 p-2 space-y-1">
          <div className="flex items-center justify-between">
            <label className="text-xs font-medium text-emerald-800">Production</label>
            {isProductionDeployed && hostingSettings?.deploy_version && (
              <span className="text-[10px] text-emerald-700">v{hostingSettings.deploy_version}</span>
            )}
          </div>
          <div className="flex items-center gap-1">
            <code className="text-xs bg-white border border-emerald-200 px-2 py-1 rounded flex-1 truncate text-emerald-900">{productionUrl}</code>
            <Button variant="ghost" size="icon" className="h-7 w-7 shrink-0" onClick={() => copyToClipboard(productionUrl, 'production')}>
              <Copy className="h-3.5 w-3.5" />
            </Button>
            <a href={productionUrl} target="_blank" rel="noopener noreferrer">
              <Button variant="ghost" size="icon" className={`h-7 w-7 shrink-0 ${!isProductionDeployed ? 'opacity-40' : ''}`}>
                <ExternalLink className="h-3.5 w-3.5" />
              </Button>
            </a>
          </div>
          {isProductionDeployed && hostingSettings?.last_deployed_at && (
            <p className="text-[10px] text-emerald-700">
              Last deployed: {new Date(hostingSettings.last_deployed_at).toLocaleString()}
            </p>
          )}
          {!isProductionDeployed && (
            <p className="text-[10px] text-emerald-600">Not promoted to production yet — call dc__promote_app after sandbox testing.</p>
          )}
        </div>

        {/* Subdomain URL */}
        <div>
          <label className="text-xs text-muted-foreground">Subdomain URL</label>
          <div className="flex items-center gap-1 mt-0.5">
            <code className="text-xs bg-muted px-2 py-1 rounded flex-1 truncate">{subdomainUrl}</code>
            <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => copyToClipboard(subdomainUrl, 'subdomain')}>
              <Copy className="h-3.5 w-3.5" />
            </Button>
          </div>
        </div>

        {/* Custom Domain */}
        <div>
          <label className="text-xs text-muted-foreground flex items-center gap-1">
            <Globe className="h-3 w-3" /> Custom Domain
          </label>
          <div className="flex items-center gap-2 mt-0.5">
            <select
              className="text-xs h-8 border rounded px-1 bg-background"
              value={bindEnv}
              onChange={(e) => setBindEnv(e.target.value === 'sandbox' ? 'sandbox' : 'production')}
            >
              <option value="production">production zip</option>
              <option value="sandbox">sandbox zip</option>
            </select>
            <Input
              className="text-xs h-8"
              placeholder="www.droneapp.co.za"
              value={domainInput}
              onChange={(e) => setDomainInput(e.target.value)}
            />
            <Button size="sm" className="text-xs h-8" onClick={handleSaveDomain} disabled={saving || !domainInput.trim()}>
              {saving ? 'Binding...' : 'Bind'}
            </Button>
          </div>
          {customDomain && (
            <div className="mt-1.5 space-y-1">
              <div className="flex items-center gap-2 flex-wrap">
                <Badge variant={sslStatus === 'active' ? 'default' : 'secondary'} className="text-[10px]">
                  TLS: {sslStatus || 'pending_dns'}
                </Badge>
                <span className="text-[10px] text-muted-foreground">
                  {hostingSettings?.custom_domain_environment || bindEnv} zip
                </span>
                <a href={`https://${customDomain}`} target="_blank" rel="noopener noreferrer" className="text-xs text-blue-600 hover:underline">
                  {customDomain}
                </a>
                {sslStatus && sslStatus !== 'active' && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="text-[10px] h-6"
                    disabled={saving}
                    onClick={async () => {
                      setSaving(true);
                      setError(null);
                      try {
                        await setCustomDomain(appId, customDomain, { environment: bindEnv, confirm_tls: true });
                        onRefresh?.();
                      } catch (err: any) {
                        setError(err.message);
                      } finally {
                        setSaving(false);
                      }
                    }}
                  >
                    Mark TLS live
                  </Button>
                )}
              </div>
              {hostingSettings?.dns_instructions && (
                <div className="bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 rounded p-2 text-xs space-y-1">
                  <p className="font-medium text-amber-800 dark:text-amber-200">DNS + Cloudflare (founder)</p>
                  {(hostingSettings.dns_instructions.records || [{
                    type: hostingSettings.dns_instructions.type,
                    host: hostingSettings.dns_instructions.host,
                    value: hostingSettings.dns_instructions.value,
                    ttl: hostingSettings.dns_instructions.ttl,
                  }]).map((rec) => (
                    <p key={`${rec.host}-${rec.value}`} className="text-amber-700 dark:text-amber-300">
                      <strong>{rec.type}</strong> host <code className="bg-amber-100 dark:bg-amber-900 px-1 rounded">{rec.host}</code>
                      {' '}→ <code className="bg-amber-100 dark:bg-amber-900 px-1 rounded">{rec.value}</code>
                    </p>
                  ))}
                  <p className="text-[10px] text-amber-700 dark:text-amber-300">
                    Orange-cloud in Cloudflare. SSL mode Full (not Strict). Not certbot.
                    After HTTPS serves the zip, click Mark TLS live. Add both names in Firebase authorized domains (Console).
                  </p>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Deploy Size (production) */}
        {isProductionDeployed && hostingSettings?.deploy_size_bytes && (
          <div className="bg-muted/50 rounded p-2 text-xs">
            <span className="text-muted-foreground">Production size:</span> {formatBytes(hostingSettings.deploy_size_bytes)}
          </div>
        )}

        {/* Deploy Command */}
        <div>
          <Button
            variant="outline"
            size="sm"
            className="w-full text-xs"
            onClick={() => copyToClipboard(deployCommand, 'deploy')}
          >
            <Upload className="h-3 w-3 mr-1.5" />
            {copied === 'deploy' ? 'Copied!' : 'Copy Deploy Command'}
          </Button>
        </div>

        {/* Environment Info */}
        <details className="text-xs">
          <summary className="cursor-pointer text-muted-foreground hover:text-foreground">Frontend environment config</summary>
          <pre className="bg-muted p-2 rounded mt-1 overflow-x-auto text-[10px] leading-4">{`// Auto-injected as env-config.js on deploy
window.__DEVCOCKPIT__ = {
  API_BASE_URL: "${publicBaseUrl}",
  APP_ID: "${appId}",
  TENANT_ID: "${tenantId}"
};`}</pre>
        </details>

        {copied && !['deploy'].includes(copied) && (
          <p className="text-xs text-green-600">Copied {copied} URL to clipboard ✓</p>
        )}
        {error && <p className="text-xs text-red-500">{error}</p>}
      </CardContent>
    </Card>
  );
}
